import { useMemo } from "react";

import type { SchemaBasedDataProvider } from "./types";

import { useModuleConfigurationSchema } from "@/features/integrations/hooks/useModuleConfiguration";
import { createSchemaBasedProvider } from "@/features/integrations/modules/job-sync/providers/createSchemaBasedProvider";
import { useAuth } from "@/lib/hooks/useAuth";

interface UseSchemaBasedProviderParams {
  /** Integration name (e.g. "indeed", "werkzoeken"). Normalized to lowercase internally. */
  integrationName: string | undefined;
  /** Module name as stored on the backend (e.g. "organic", "job-sync"). */
  moduleName: string;
  /** Translation sub-key namespace (e.g. "organic", "jobSync"). */
  moduleSubKey: string;
  /** When provided, schema is fetched at client level; otherwise at tenant level. */
  clientId?: string | null;
}

/**
 * Loads the UI schema for a given integration/module from the server and wraps it
 * in a `SchemaBasedDataProvider`. Replaces per-module schema hooks
 * (e.g. `useJobSyncDataProvider`, `useOrganicModuleSchema`).
 */
export function useSchemaBasedProvider({
  integrationName,
  moduleName,
  moduleSubKey,
  clientId,
}: UseSchemaBasedProviderParams): SchemaBasedDataProvider {
  const { tenantId } = useAuth();
  const normalizedName = integrationName?.toLowerCase() || "";

  const schemaQuery = useModuleConfigurationSchema(
    normalizedName,
    moduleName,
    clientId
      ? {
        level: "client" as const,
        tenantId: tenantId ?? null,
        clientId,
      }
      : {
        level: "tenant" as const,
        tenantId: tenantId ?? null,
      },
    !!normalizedName,
  );

  return useMemo(() => {
    if (!normalizedName) {
      return createSchemaBasedProvider("", undefined, moduleSubKey);
    }
    return createSchemaBasedProvider(
      normalizedName,
      schemaQuery.data,
      moduleSubKey,
    );
  }, [normalizedName, moduleSubKey, schemaQuery.data]);
}
